import * as THREE from './uae3d-three.module.js';
import { GLTFLoader } from './uae3d-gltf-loader.js';
import { OrbitControls } from './uae3d-orbit-controls.js';
import { createProductInteraction } from './uae3d-interactions.js';

const optionLabels = {
  espresso: ['Ground coffee', 'Small capsule', 'Large capsule'],
  vacuum: ['Standard', 'Brush', 'Crevice', 'Blower', 'Inflator'],
  led: ['Light bar', 'Round light']
};

function start(section) {
  if (section.dataset.uae3dReady) return;
  section.dataset.uae3dReady = 'true';
  const assets = JSON.parse(section.querySelector('[data-uae3d-assets]').textContent);
  const tabs = [...section.querySelectorAll('[data-model]')];
  if (!tabs.length) return;
  const stage = section.querySelector('[data-stage]');
  const status = section.querySelector('[data-status]');
  const variants = section.querySelector('[data-variants]');
  const actionHost = section.querySelector('[data-uae3d-controls]');
  const title = section.querySelector('[data-title]');
  const description = section.querySelector('[data-description]');
  const price = section.querySelector('[data-price]');
  const index = section.querySelector('[data-index]');
  const shop = section.querySelector('[data-shop]');
  const fold = section.querySelector('[data-fold]');
  const reset = section.querySelector('[data-reset]');
  const pickerToggle = section.querySelector('[data-uae-picker-toggle]');
  const pickerMenu = pickerToggle ? section.querySelector('.uae3d__tabs') : null;
  function setPickerOpen(open) {
    if (!pickerToggle) return;
    section.classList.toggle('is-picker-open', open);
    pickerToggle.setAttribute('aria-expanded', String(open));
    if (pickerMenu) pickerMenu.inert = !open;
  }
  if (pickerToggle) {
    section.classList.add('is-picker-ready');
    setPickerOpen(false);
    pickerToggle.addEventListener('click', () => setPickerOpen(!section.classList.contains('is-picker-open')));
    document.addEventListener('pointerdown', event => {
      if (section.classList.contains('is-picker-open') && !pickerToggle.contains(event.target) && !pickerMenu.contains(event.target)) setPickerOpen(false);
    });
    section.addEventListener('keydown', event => {
      if (event.key === 'Escape' && section.classList.contains('is-picker-open')) {
        setPickerOpen(false);
        pickerToggle.focus();
      }
    });
  }
  let renderer;
  try {
    renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: 'high-performance' });
  } catch (_) {
    status.textContent = '3D preview is unavailable on this device.';
    return;
  }
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.35));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.04;
  stage.appendChild(renderer.domElement);
  const scene = new THREE.Scene();
  const studio = new THREE.Scene();
  studio.background = new THREE.Color(0x919498);
  for (const [color, width, height, x, y, z, rotation] of [
    [0xffffff, 3.1, 4, -2, 2, 2, .55],
    [0xdcecff, 2.1, 3, 2, 1, -1, -.75],
    [0xffe7ce, 3, 1.5, .2, 3, 1, 0]
  ]) {
    const card = new THREE.Mesh(new THREE.PlaneGeometry(width, height), new THREE.MeshBasicMaterial({ color, side: THREE.DoubleSide }));
    card.position.set(x, y, z);
    card.rotation.y = rotation;
    studio.add(card);
  }
  const pmrem = new THREE.PMREMGenerator(renderer);
  const environment = pmrem.fromScene(studio, .035);
  scene.environment = environment.texture;
  pmrem.dispose();
  scene.add(new THREE.HemisphereLight(0xffffff, 0xb7aba0, 1.25));
  const key = new THREE.DirectionalLight(0xffffff, 1.8);
  key.position.set(4, 7, 5);
  scene.add(key);
  const fill = new THREE.DirectionalLight(0xd5e8ff, .8);
  fill.position.set(-5, 2, -4);
  scene.add(fill);
  const shadowCanvas = document.createElement('canvas');
  shadowCanvas.width = shadowCanvas.height = 256;
  const shadowContext = shadowCanvas.getContext('2d');
  const shadowGradient = shadowContext.createRadialGradient(128, 128, 8, 128, 128, 126);
  shadowGradient.addColorStop(0, 'rgba(40,37,31,.20)');
  shadowGradient.addColorStop(.52, 'rgba(40,37,31,.07)');
  shadowGradient.addColorStop(1, 'rgba(40,37,31,0)');
  shadowContext.fillStyle = shadowGradient;
  shadowContext.fillRect(0, 0, 256, 256);
  const shadowTexture = new THREE.CanvasTexture(shadowCanvas);
  const contactShadow = new THREE.Mesh(new THREE.PlaneGeometry(3.2, 2.3), new THREE.MeshBasicMaterial({ map: shadowTexture, transparent: true, depthWrite: false, opacity: .75 }));
  contactShadow.rotation.x = -Math.PI / 2;
  scene.add(contactShadow);
  const camera = new THREE.PerspectiveCamera(36, 1, .1, 100);
  camera.position.set(3.7, 2.5, 4.8);
  const controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;
  controls.dampingFactor = .07;
  controls.enablePan = false;
  controls.minDistance = 2.7;
  controls.maxDistance = 8;
  controls.minPolarAngle = .28;
  controls.maxPolarAngle = Math.PI - .28;
  const loader = new GLTFLoader();
  const cache = new Map();
  let current = null;
  let currentGroup = '';
  let currentVariant = 0;
  let currentTab = null;
  let interaction = null;
  let requestId = 0;
  let folded = false;
  let visible = false;
  let pendingModel = null;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function resize() {
    const width = Math.max(1, stage.clientWidth);
    const height = Math.max(1, stage.clientHeight);
    camera.aspect = width / height;
    camera.updateProjectionMatrix();
    renderer.setSize(width, height, false);
  }
  const resizeObserver = new ResizeObserver(resize);
  resizeObserver.observe(stage);
  resize();
  const viewportObserver = new IntersectionObserver(entries => {
    visible = entries[0].isIntersecting;
    if (visible && pendingModel) {
      const pending = pendingModel;
      pendingModel = null;
      showModel(pending.group, pending.variant);
    }
  }, { rootMargin: '200px' });
  viewportObserver.observe(section);

  function setLoading(message) {
    status.textContent = message;
    status.hidden = !message;
  }
  const cameraHomes = {
    glasses: [3.7, 2.5, 4.8], fan: [3.6, 2.1, 4.9], espresso: [3, 1.8, 5.2],
    vacuum: [3.5, 2.1, 5], compressor: [3.5, 2.2, 5], jumpstarter: [3.4, 2.4, 5.4],
    led: [2.8, 2.1, 5.7], projector: [3.1, 2.4, 5.2], hy300: [3.1, 2.4, 5.2], hy300: [3.1, 2.4, 5.2], hy300: [3.1, 2.4, 5.2], hy300: [3.1, 2.4, 5.2], hy300: [3.1, 2.4, 5.2], hy300: [3.1, 2.4, 5.2], hy300: [3.1, 2.4, 5.2], hy300: [3.1, 2.4, 5.2]
  };
  function moveCamera(point) {
    const from = { x: camera.position.x, y: camera.position.y, z: camera.position.z };
    const target = { x: point[0], y: point[1], z: point[2] };
    if (!reducedMotion && window.gsap) {
      window.gsap.to(from, { ...target, duration: .85, ease: 'power2.inOut', onUpdate() {
        camera.position.set(from.x, from.y, from.z);
        controls.target.set(0, 0, 0);
      } });
    } else {
      camera.position.set(target.x, target.y, target.z);
      controls.target.set(0, 0, 0);
    }
  }
  function resetView() { moveCamera(cameraHomes[currentGroup] || cameraHomes.glasses); }
  function setFold(next) {
    folded = next;
    if (!current) return;
    for (const [name, direction] of [['LeftFoldingArm', -1], ['RightFoldingArm', 1]]) {
      const pivot = current.getObjectByName(name);
      if (!pivot) continue;
      const angle = next ? direction * 1.38 : 0;
      if (!reducedMotion && window.gsap) window.gsap.to(pivot.rotation, { y: angle, duration: .7, ease: 'power2.inOut', overwrite: true });
      else pivot.rotation.y = angle;
    }
    fold.textContent = next ? 'Open arms' : 'Fold arms';
  }
  async function showModel(group, variant) {
    const url = assets[group]?.[variant];
    if (!url) return;
    const id = ++requestId;
    if (!visible) {
      pendingModel = { group, variant };
      return;
    }
    currentGroup = group;
    currentVariant = variant;
    setLoading('Loading 3D preview…');
    try {
      let model = cache.get(url);
      if (!model) {
        const asset = await import(new URL(url, document.baseURI).href);
        const gltf = await loader.loadAsync(asset.default);
        model = gltf.scene;
        cache.set(url, model);
      }
      if (id !== requestId) return;
      if (current) scene.remove(current);
      if (interaction) { interaction.dispose(); interaction = null; }
      current = model.clone(true);
      scene.add(current);
      current.scale.setScalar(1);
      current.position.set(0, 0, 0);
      current.rotation.set(0, 0, 0);
      current.updateMatrixWorld(true);
      const box = new THREE.Box3().setFromObject(current);
      const center = box.getCenter(new THREE.Vector3());
      const size = box.getSize(new THREE.Vector3());
      const scale = 2.85 / Math.max(size.x, size.y, size.z, .01);
      current.scale.setScalar(scale);
      current.position.copy(center.multiplyScalar(-scale));
      contactShadow.position.set(0, box.min.y * scale + current.position.y + .035, 0);
      contactShadow.visible = group !== 'glasses' && group !== 'led';
      if (group === 'glasses') current.rotation.y = -.1;
      folded = false;
      fold.hidden = group !== 'glasses';
      fold.textContent = 'Fold arms';
      interaction = createProductInteraction({ model: current, group, canvas: renderer.domElement, camera, controls, scene, actionHost, reducedMotion });
      interaction.setViewCamera(view => {
        const home = cameraHomes[group] || cameraHomes.glasses;
        moveCamera(view === 'front' ? [0, home[1], 5.7] : [5.7, home[1], 0]);
      });
      if (group === 'espresso' && variant > 0) interaction.revealVariant?.();
      resetView();
      setLoading('');
      renderer.domElement.style.opacity = '1';
    } catch (error) {
      console.error('3D product failed to load', error);
      setLoading('3D preview is temporarily unavailable. You can still view the product.');
    }
  }
  function selectTab(tab) {
    setPickerOpen(false);
    currentTab = tab;
    const group = tab.dataset.model;
    section.dataset.uae3dModel = group;
    tabs.forEach((item, i) => {
      item.setAttribute('aria-selected', String(item === tab));
      item.tabIndex = item === tab ? 0 : -1;
      if (item === tab && index) index.textContent = String(i + 1).padStart(2, '0') + ' / ' + String(tabs.length).padStart(2, '0');
    });
    title.textContent = tab.dataset.title;
    description.textContent = tab.dataset.description;
    price.textContent = tab.dataset.productPrice || '';
    price.hidden = !tab.dataset.productPrice;
    const productUrl = tab.dataset.productUrl;
    shop.hidden = !productUrl;
    if (productUrl) shop.href = productUrl;
    else shop.removeAttribute('href');
    variants.replaceChildren();
    actionHost.replaceChildren();
    const labels = optionLabels[group] || [];
    labels.forEach((label, i) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'uae3d__variant';
      button.textContent = label;
      button.setAttribute('aria-pressed', String(i === 0));
      button.addEventListener('click', () => {
        variants.querySelectorAll('button').forEach(node => node.setAttribute('aria-pressed', String(node === button)));
        showModel(group, i);
      });
      variants.appendChild(button);
    });
    showModel(group, 0);
  }
  tabs.forEach((tab, i) => {
    tab.addEventListener('click', () => selectTab(tab));
    tab.addEventListener('keydown', event => {
      if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
      event.preventDefault();
      const next = (i + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
      tabs[next].focus();
      selectTab(tabs[next]);
    });
  });
  section.addEventListener('uae3d:select', event => {
    const tab = tabs.find(item => item.dataset.model === event.detail?.model);
    if (tab) {
      selectTab(tab);
      delete section.dataset.pendingModel;
    }
  });
  reset.addEventListener('click', () => { interaction?.reset(); if (currentGroup === 'glasses') setFold(false); resetView(); });
  fold.addEventListener('click', () => setFold(!folded));
  selectTab(tabs.find(item => item.dataset.model === section.dataset.pendingModel) || tabs[0]);
  delete section.dataset.pendingModel;
  let lastRendered = 0;
  function frame(now) {
    if (!document.body.contains(section)) {
      resizeObserver.disconnect();
      viewportObserver.disconnect();
      controls.dispose();
      interaction?.dispose();
      environment.dispose();
      shadowTexture.dispose();
      renderer.dispose();
      return;
    }
    requestAnimationFrame(frame);
    if (!visible || document.hidden) return;
    if (now - lastRendered < 30) return;
    lastRendered = now;
    interaction?.update(performance.now());
    controls.update();
    renderer.render(scene, camera);
  }
  frame();
}

function mount() {
  document.querySelectorAll('[data-uae3d]').forEach(start);
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mount);
else mount();
document.addEventListener('shopify:section:load', mount);
